import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Card,
  Tag,
  Button,
  Space,
  Select,
  Input,
  Typography,
  Alert,
  Popconfirm,
  message,
  Progress
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { useStores } from '../../../stores';
import partnerService from '../../../services/partnerService';
import './Inventory.scss';

const { Title, Text } = Typography;
const { Option } = Select;

const InventoryList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerInventoryStore } = useStores();
  const { filteredInventory, loading, error, searchQuery, stockFilter } = partnerInventoryStore;
  const [itemsInfo, setItemsInfo] = useState(null);

  useEffect(() => {
    partnerInventoryStore.fetchInventory();
    fetchItemsInfo();
  }, [partnerInventoryStore]);

  const fetchItemsInfo = async () => {
    const result = await partnerService.fetchItemsInfo();
    if (result.success) {
      setItemsInfo(result.data);
    }
  };

  const handleDelete = async (inventoryId) => {
    const result = await partnerInventoryStore.deleteInventory(inventoryId);
    if (result.success) {
      message.success(t('partner.products.messages.deactivateSuccess'));
    } else {
      message.error(result.error || t('partner.products.deleteFailed'));
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) {
      return { color: 'red', text: t('partner.products.stockStatus.outOfStock') };
    } else if (quantity <= 10) {
      return { color: 'orange', text: t('partner.products.stockStatus.lowStock') };
    } else {
      return { color: 'green', text: t('partner.products.stockStatus.inStock') };
    }
  };

  const columns = [
    {
      title: t('partner.products.tableHeaders.product'),
      key: 'product',
      render: (_, record) => (
        <div>
          <div><strong>{record.product_name}</strong></div>
          <div style={{ fontSize: 12, color: '#666' }}>{t('partner.products.sku')}: {record.sku}</div>
          {record.color && <div style={{ fontSize: 12, color: '#666' }}>{t('partner.products.color')}: {record.color}</div>}
          {record.size && <div style={{ fontSize: 12, color: '#666' }}>{t('partner.products.size')}: {record.size}</div>}
        </div>
      ),
    },
    {
      title: t('partner.products.tableHeaders.price'),
      dataIndex: 'price',
      key: 'price',
      width: 120,
      align: 'right',
      render: (price) => `€${parseFloat(price).toFixed(2)}`,
      sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price),
    },
    {
      title: t('partner.products.tableHeaders.shipping'),
      dataIndex: 'shipping_cost',
      key: 'shipping_cost',
      width: 120,
      align: 'right',
      render: (cost) => `€${parseFloat(cost).toFixed(2)}`,
    },
    {
      title: t('partner.products.tableHeaders.stock'),
      dataIndex: 'stock_quantity',
      key: 'stock_quantity',
      width: 120,
      align: 'center',
      render: (quantity) => {
        const status = getStockStatus(quantity);
        return (
          <Tag color={status.color}>
            {quantity} - {status.text}
          </Tag>
        );
      },
      sorter: (a, b) => a.stock_quantity - b.stock_quantity,
    },
    {
      title: t('partner.products.tableHeaders.active'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('partner.products.tableHeaders.actions'),
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/partner/inventory/edit/${record.id}`)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('partner.products.deactivateConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              disabled={!record.is_active}
            >
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const isLimitReached = itemsInfo && itemsInfo.items_remaining <= 0;
  const usagePercent = itemsInfo ? (itemsInfo.current_items / itemsInfo.max_items) * 100 : 0;

  return (
    <div className="inventory-list">
      <div className="page-header">
        <Title level={2}>{t('partner.products.title')}</Title>
        {!isLimitReached ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/partner/inventory/add')}
          >
            {t('partner.products.addProduct')}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<ShopOutlined />}
            onClick={() => navigate('/partner/packages')}
          >
            {t('partner.products.getMoreSlots')}
          </Button>
        )}
      </div>

      {/* Item Usage Info */}
      {itemsInfo && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <div style={{ flex: 1, maxWidth: 300 }}>
              <Text type="secondary">{t('partner.products.itemUsage')}</Text>
              <Progress
                percent={Math.round(usagePercent)}
                status={isLimitReached ? 'exception' : usagePercent >= 80 ? 'normal' : 'active'}
                strokeColor={isLimitReached ? '#ff4d4f' : usagePercent >= 80 ? '#faad14' : '#1890ff'}
                size="small"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {itemsInfo.current_items} / {itemsInfo.max_items} {t('partner.products.itemsLabel')}
              </Text>
            </div>
            {itemsInfo.items_remaining <= 3 && itemsInfo.items_remaining > 0 && (
              <Text type="warning">
                {itemsInfo.items_remaining} {t('partner.products.slotsRemaining')}
              </Text>
            )}
          </Space>
        </Card>
      )}

      {/* Limit Reached Alert */}
      {isLimitReached && (
        <Alert
          message={t('partner.products.limitReached')}
          description={
            <span>
              {t('partner.products.limitReachedDesc')}{' '}
              <Link to="/partner/packages">{t('partner.products.getMoreSlots')}</Link>
            </span>
          }
          type="warning"
          showIcon
          icon={<ShopOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        {/* Filters */}
        <Space size="middle" style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder={t('partner.products.searchPlaceholder')}
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 300 }}
            value={searchQuery}
            onChange={(e) => partnerInventoryStore.setSearchQuery(e.target.value)}
          />

          <Select
            placeholder={t('partner.products.filterByStock')}
            allowClear
            style={{ width: 180 }}
            value={stockFilter}
            onChange={(value) => partnerInventoryStore.setStockFilter(value)}
          >
            <Option value="IN_STOCK">{t('partner.products.stockStatus.inStock')}</Option>
            <Option value="LOW_STOCK">{t('partner.products.stockStatus.lowStock')}</Option>
            <Option value="OUT_OF_STOCK">{t('partner.products.stockStatus.outOfStock')}</Option>
          </Select>
        </Space>

        {/* Inventory Table */}
        <Table
          columns={columns}
          dataSource={filteredInventory}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${total} ${t('partner.products.totalProducts')}`,
          }}
          locale={{
            emptyText: t('partner.products.noProducts'),
          }}
        />
      </Card>
    </div>
  );
});

export default InventoryList;
